/**
 * ComplianceGrid — mode-aware dispatcher.
 *
 * Reads the admin config `validation.checklist_mode` to decide which
 * compliance grid to render on the loan-file detail page:
 *
 *   - 'cam_vs_los' → CamComparisonGrid (V2 — 12-point LOS vs CAM)
 *   - '17_point' / 'full' / unset → ChecklistGrid (V1 — 17-point LOS-only)
 *
 * This way both modes coexist; flipping the admin config switches the UI
 * with no other changes. Falls back to V1 on any config-read error so
 * existing deployments never break.
 *
 * The loan-file detail page mounts this instead of the specific grids
 * directly.
 */

import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/api/other-apis';
import { Card } from '@/acc-ui/primitives/Layout';
import { ChecklistGrid } from './ChecklistGrid';
import { CamComparisonGrid } from './CamComparisonGrid';

const CHECKLIST_MODE_KEY = 'validation.checklist_mode';

export function ComplianceGrid({ loanFileId }: { loanFileId: string }) {
  // The config doesn't change often; cache for 5 minutes. This avoids
  // hammering the admin endpoint on every loan-file page open.
  const configQuery = useQuery({
    queryKey: ['adminConfig', CHECKLIST_MODE_KEY],
    queryFn: async () => {
      const all = await adminApi.listConfig();
      const entry = all.find((e) => e.key === CHECKLIST_MODE_KEY);
      // The value field is unknown — could be a string like "cam_vs_los"
      // or "17_point". Treat anything non-string as default.
      const raw = entry?.value;
      if (typeof raw === 'string') return raw;
      return '17_point';
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  // While the mode is unknown, render a fixed-height placeholder rather than
  // guessing a grid. Guessing V1 and then swapping to V2 once config resolves
  // causes a whole-component layout shift on every page open. The placeholder
  // reserves the grid's space so the swap-in is seamless.
  if (configQuery.isLoading) {
    return (
      <Card>
        <div className="animate-pulse" style={{ minHeight: 288 }} aria-hidden />
      </Card>
    );
  }

  // Fall back to V1 grid only if the lookup actually failed.
  const mode = configQuery.data ?? '17_point';

  if (mode === 'cam_vs_los') {
    return <CamComparisonGrid loanFileId={loanFileId} />;
  }
  return <ChecklistGrid loanFileId={loanFileId} />;
}
